import { useMutation, useQuery } from '@tanstack/react-query';

import reviewTemplateService from './reviewTemplateService';

import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { ManagerAssessmentSkillSelectorOptionValue } from '@/constants/review/ManagerAssessmentSkillSelectorOption';
import type { PaginatedQueryParams, PaginatedRequestParams } from '@/types/api';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { ReviewTemplate } from '@/types/review/ReviewTemplate';

import { queryClient } from '@/AppWithProviders';
import { ReviewTemplateQueryKey, ReviewTemplateMutationKey } from '@/constants/QueryKeys';
import { useAmelioInfiniteQuery } from '@/hooks/api/useAmelioInfiniteQuery';

type ReviewTemplatesQueryParams = PaginatedQueryParams<ReviewTemplate> & {
    initialPageSize?: number;
    subsequentPageSize?: number;
};

const useReviewTemplates = ({
    pageSize,
    options,
    meta,
    initialPageSize,
    subsequentPageSize,
}: ReviewTemplatesQueryParams) => {
    const getPageSizeForPage = (pageNumber: number) => {
        if (!initialPageSize || !subsequentPageSize) {
            return pageSize;
        }
        return pageNumber === 1 ? initialPageSize : subsequentPageSize;
    };

    return useAmelioInfiniteQuery<ReviewTemplate>({
        queryKey: [
            ReviewTemplateQueryKey.TEMPLATE,
            meta?.reviewType,
            initialPageSize,
            subsequentPageSize,
        ],
        queryFn: ({
            pageNumber,
            pageSize: currentPageSize,
            skipCount,
            meta: queryMeta,
        }: PaginatedRequestParams) =>
            reviewTemplateService.getReviewTemplates({
                pageNumber,
                pageSize: currentPageSize,
                skipCount,
                meta: queryMeta,
            }),
        pageSize: subsequentPageSize ?? pageSize,
        getPageSizeForPage,
        options,
        meta,
    });
};

const useManageReviewTemplates = ({
    pageSize,
    options,
    meta,
    initialPageSize,
    subsequentPageSize,
}: ReviewTemplatesQueryParams) => {
    const getPageSizeForPage = (pageNumber: number) => {
        if (!initialPageSize || !subsequentPageSize) {
            return pageSize;
        }
        return pageNumber === 1 ? initialPageSize : subsequentPageSize;
    };

    return useAmelioInfiniteQuery<ReviewTemplate>({
        queryKey: [
            ReviewTemplateQueryKey.TEMPLATE,
            'manage',
            meta?.reviewType,
            initialPageSize,
            subsequentPageSize,
        ],
        queryFn: ({
            pageNumber,
            pageSize: currentPageSize,
            skipCount,
            meta: queryMeta,
        }: PaginatedRequestParams) =>
            reviewTemplateService.getManageReviewTemplates({
                pageNumber,
                pageSize: currentPageSize,
                skipCount,
                meta: queryMeta,
            }),
        pageSize: subsequentPageSize ?? pageSize,
        getPageSizeForPage,
        options,
        meta,
    });
};

const useAmelioReviewTemplates = (
    reviewType: EvaluationTypeValue | undefined,
    options?: { enabled?: boolean },
) => {
    return useQuery<ReviewTemplate[]>({
        queryKey: [ReviewTemplateQueryKey.TEMPLATE, 'amelio', reviewType],
        queryFn: () => reviewTemplateService.getAmelioTemplates(reviewType!),
        enabled: !!reviewType && (options?.enabled ?? true),
    });
};

const useCreateReviewTemplate = () => {
    return useMutation({
        mutationKey: [ReviewTemplateMutationKey.UPDATE_TEMPLATE_CARD],
        mutationFn: ({
            reviewType,
            skillSelectorOption,
        }: {
            reviewType: EvaluationTypeValue;
            skillSelectorOption?: ManagerAssessmentSkillSelectorOptionValue | null;
        }) => reviewTemplateService.createReviewTemplate(reviewType, skillSelectorOption),
        onSuccess: ({ reviewType }) => {
            queryClient.invalidateQueries({
                queryKey: [ReviewTemplateQueryKey.TEMPLATE, reviewType],
            });
        },
    });
};

const useUpdateReviewTemplate = () => {
    return useMutation({
        mutationKey: [ReviewTemplateMutationKey.UPDATE_TEMPLATE_CARD],
        mutationFn: (reviewTemplate: ReviewTemplate) =>
            reviewTemplateService.updateReviewTemplate(reviewTemplate),
        onSuccess: ({ id, reviewType }) => {
            queryClient.invalidateQueries({
                queryKey: [ReviewTemplateQueryKey.TEMPLATE, reviewType],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewTemplateQueryKey.TEMPLATE, 'manage', reviewType],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewTemplateQueryKey.TEMPLATE, id],
            });
        },
    });
};

const useDeleteReviewTemplate = () => {
    return useMutation({
        mutationKey: [ReviewTemplateMutationKey.UPDATE_TEMPLATE_CARD],
        mutationFn: (reviewTemplateId: string) =>
            reviewTemplateService.deleteReviewTemplate(reviewTemplateId),
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: [ReviewTemplateQueryKey.TEMPLATE, data.reviewType],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewTemplateQueryKey.TEMPLATE, 'manage', data.reviewType],
            });
        },
    });
};

const useGetReviewTemplate = (reviewTemplateId?: string) => {
    return useQuery<ReviewTemplate>({
        queryKey: [ReviewTemplateQueryKey.TEMPLATE, reviewTemplateId],
        queryFn: () => reviewTemplateService.getReviewTemplate(reviewTemplateId!),
        enabled: !!reviewTemplateId,
    });
};

const useGetReviewTemplatePreviewQuestions = (options?: { limit?: number; enabled?: boolean }) => {
    const { limit, enabled } = options || {};
    return useQuery<ReviewQuestion[]>({
        queryKey: [ReviewTemplateQueryKey.PREVIEW_QUESTIONS, limit ?? 10],
        queryFn: () =>
            reviewTemplateService.getReviewTemplatePreviewQuestions({
                limit,
            }),
        enabled: enabled ?? true,
    });
};

export {
    useReviewTemplates,
    useManageReviewTemplates,
    useGetReviewTemplate,
    useGetReviewTemplatePreviewQuestions,
    useCreateReviewTemplate,
    useUpdateReviewTemplate,
    useDeleteReviewTemplate,
    useAmelioReviewTemplates,
};
